import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../context/api";
import "../styles/UserPayment.css";

const USER_PAYMENT_CACHE_TTL_MS = 60 * 1000;

export const UserPayment = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem("role") || "";
  const isSuperAdmin = role === "superadmin";
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDropdown, setShowDayDropdown] = useState(false);
  const [userWinners, setUserWinners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Set of winner ids currently being paid; lets independent payments run concurrently.
  const [processingPayments, setProcessingPayments] = useState(() => new Set());
  // Ref (not state) so a fast double-click/tap is blocked before React re-renders the disabled button.
  const paymentLockRef = useRef(new Set());

  const dayOptions = [
    "SUNDAY ASEDA",
    "MONDAY SPECIAL",
    "LUCKY TUESDAY",
    "MIDWEEK",
    "FORTUNE THURSDAY",
    "FRIDAY BONANZA",
    "NATIONAL WEEKLY",
    "NLA VAG MONDAY",
    "NLA NOON RUSH MONDAY",
    "HUNTEX MONDAY",
    "NLA VAG TUESDAY",
    "NLA NOON RUSH TUESDAY",
    "HUNTEX AKOMA TUESDAY",
    "NLA VAG WEDNESDAY",
    "NLA NOON RUSH WEDNESDAY",
    "HUNTEX WEDNESDAY",
    "NLA VAG THURSDAY",
    "NLA NOON RUSH THURSDAY",
    "HUNTEX THURSDAY",
    "NLA VAG FRIDAY",
    "NLA NOON RUSH FRIDAY",
    "HUNTEX FRIDAY",
    "NLA VAG SATURDAY",
    "NLA NOON RUSH SATURDAY",
    "HUNTEX SATURDAY",
    "HUNTEX SUNDAY",
  ];

  const formatDateForApi = (dateValue) => {
    return `${dateValue.getMonth() + 1}/${dateValue.getDate()}/${dateValue.getFullYear()}`;
  };

  const buildCacheKey = () => {
    const dateKey = formatDateForApi(selectedDate);
    return `userPayment_winners_v2_${role || "unknown"}_${dateKey}_${selectedDay || ""}`;
  };

  const fetchUserData = async (forceRefresh = false) => {
    if (!selectedDay) {
      alert("Please select a day first");
      return;
    }

    try {
      setLoading(true);
      const formattedDate = formatDateForApi(selectedDate);
      const cacheKey = buildCacheKey();

      if (!forceRefresh) {
        const cachedRaw = localStorage.getItem(cacheKey);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          const now = Date.now();
          if (cached?.timestamp && now - cached.timestamp < USER_PAYMENT_CACHE_TTL_MS) {
            setUserWinners(Array.isArray(cached.data) ? cached.data : []);
            return;
          }
        }
      }

      const response = await api.get("/winners", {
        params: {
          selectedDate: formattedDate,
          selectedDay,
          limit: 1000,
        },
      });
      const rows = response.data || [];
      setUserWinners(rows);
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          data: rows,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.error("Error fetching user data:", error.message);
      setUserWinners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDay) {
      fetchUserData();
    }
  }, [selectedDay]);

  const markWinnerPaid = (winnerId) => {
    setUserWinners((prevWinners) =>
      prevWinners.map((userWinner) => ({
        ...userWinner,
        winners: (userWinner.winners || []).map((winner) =>
          String(winner._id) === String(winnerId)
            ? { ...winner, paid: "Paid" }
            : winner
        ),
      }))
    );
  };

  const isWinnerAlreadyPaid = (winnerId) =>
    userWinners.some((userWinner) =>
      (userWinner.winners || []).some(
        (winner) => String(winner._id) === String(winnerId) && winner.paid === "Paid"
      )
    );

  // Refetches winners fresh from the server and checks the freshly returned rows
  // directly, since stale component state wouldn't reflect a payment that just landed.
  const refetchWinnerPaidStatus = async (winnerId) => {
    if (!selectedDay) return false;

    try {
      const formattedDate = formatDateForApi(selectedDate);
      const response = await api.get("/winners", {
        params: { selectedDate: formattedDate, selectedDay, limit: 1000 },
      });
      const rows = response.data || [];
      setUserWinners(rows);
      localStorage.setItem(
        buildCacheKey(),
        JSON.stringify({ data: rows, timestamp: Date.now() })
      );

      return rows.some((userWinner) =>
        (userWinner.winners || []).some(
          (winner) => String(winner._id) === String(winnerId) && winner.paid === "Paid"
        )
      );
    } catch (refetchError) {
      return false;
    }
  };

  const handlePayment = async (phoneNumber, amount, winnerId) => {
    const paymentKey = String(winnerId);

    // Synchronous guard: blocks a second click/tap fired before the disabled state re-renders.
    if (paymentLockRef.current.has(paymentKey)) {
      return;
    }

    if (!window.confirm(`Confirm payment of GHC ${amount} to ${phoneNumber}?`)) {
      return;
    }

    paymentLockRef.current.add(paymentKey);
    setProcessingPayments((prev) => new Set(prev).add(paymentKey));

    try {
      const response = await api.post("/payment", {
        phoneNumber,
        amount,
        winnerId,
      });

      if (response.status >= 200 && response.status < 300) {
        alert("✅ Payment successful!");
        markWinnerPaid(winnerId);
      } else {
        alert("❌ Payment failed! Please try again.");
      }
    } catch (error) {
      // The request may have reached the server before the network dropped, so
      // resync with the backend before allowing a retry to avoid paying twice.
      const alreadyPaid = await refetchWinnerPaidStatus(winnerId);
      if (alreadyPaid) {
        alert("Payment was already processed. No further action needed.");
      } else {
        alert("❌ Payment failed! Please try again.");
      }
    } finally {
      paymentLockRef.current.delete(paymentKey);
      setProcessingPayments((prev) => {
        const next = new Set(prev);
        next.delete(paymentKey);
        return next;
      });
    }
  };


  const handleRefresh = () => {
    if (selectedDay) {
      fetchUserData(true);
    }
  };

  const totalWinners = userWinners.reduce((total, user) => total + (user.winners?.length || 0), 0);
  const totalPrize = userWinners.reduce((total, user) => 
    total + (user.winners?.reduce((sum, winner) => sum + (Number(winner.winPrice) || 0), 0) || 0), 0);

  return (
    <div className="payment-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">{isSuperAdmin ? "Super Admin Payment Dashboard" : "Payment Dashboard"}</h1>
            <p className="dashboard-subtitle">
              {isSuperAdmin ? "Manage super admin scoped winner payments" : "Manage winner payments efficiently"}
            </p>
          </div>
          <div className="header-right">
            <div className="user-welcome">
              <span className="welcome-text">Welcome, {isSuperAdmin ? "Super Admin" : "Admin"}</span>
              <span className="user-avatar">👨‍💼</span>
            </div>
            
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Sidebar Filters */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-card">
            <h3 className="sidebar-title">Filters</h3>
            
            <div className="filter-group">
              <label className="filter-label">
                <span className="label-icon">📅</span>
                Select Date
              </label>
              <input
                type="date"
                className="date-picker"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
              />
              <p className="selected-date-display">
                Selected: {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <span className="label-icon">📋</span>
                Select Day
              </label>
              <div className="day-dropdown-container">
                <button 
                  className="day-dropdown-toggle"
                  onClick={() => setShowDayDropdown(!showDayDropdown)}
                >
                  <span className="dropdown-selected">
                    {selectedDay || "Choose a day"}
                  </span>
                  <span className={`dropdown-arrow ${showDayDropdown ? 'open' : ''}`}>▼</span>
                </button>
                
                {showDayDropdown && (
                  <div className="day-dropdown-menu">
                    <div className="dropdown-search">
                      <input
                        type="text"
                        placeholder="Search days..."
                        className="search-input"
                        onChange={(e) => {
                          // Implement search if needed
                        }}
                      />
                    </div>
                    <div className="dropdown-options">
                      {dayOptions.map((day, index) => (
                        <button
                          key={index}
                          className="day-option"
                          onClick={() => {
                            setSelectedDay(day);
                            setShowDayDropdown(false);
                          }}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <button 
                className="search-btn"
                onClick={fetchUserData}
                disabled={loading || !selectedDay}
              >
                <span className="btn-icon">🔍</span>
                {loading ? "Searching..." : "Search Winners"}
              </button>
              
              <button 
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={loading || !selectedDay}
              >
                <span className="btn-icon">🔄</span>
                Refresh Data
              </button>
            </div>
          </div>

          {/* Stats Card */}
          <div className="stats-card">
            <h3 className="stats-title">Quick Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-icon total-users">👥</div>
                <div className="stat-info">
                  <span className="stat-value">{userWinners.length}</span>
                  <span className="stat-label">Total Users</span>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon total-winners">🏆</div>
                <div className="stat-info">
                  <span className="stat-value">{totalWinners}</span>
                  <span className="stat-label">Winners</span>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon total-prize">💰</div>
                <div className="stat-info">
                  <span className="stat-value">GHC {totalPrize.toLocaleString()}</span>
                  <span className="stat-label">Total Prize</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="dashboard-content">
          {/* Header Stats */}
          <div className="content-header">
            <div className="header-info">
              <h2 className="content-title">
                {selectedDay ? `${selectedDay} Winners` : "Select a day to view winners"}
              </h2>
              <p className="content-subtitle">
                {selectedDate.toLocaleDateString('en-US', { dateStyle: 'full' })}
              </p>
            </div>
            <div className="header-actions">
              <button className="export-btn">
                <span className="btn-icon">📊</span>
                Export Report
              </button>
              <button className="bulk-pay-btn">
                <span className="btn-icon">💸</span>
                Bulk Payment
              </button>
            </div>
          </div>

          {/* Winners List */}
          <div className="winners-container">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading winners data...</p>
              </div>
            ) : userWinners.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>No Winners Found</h3>
                <p>
                  {selectedDay 
                    ? `No winners found for ${selectedDay} on ${selectedDate.toLocaleDateString()}`
                    : "Please select a day from the sidebar to view winners"
                  }
                </p>
              </div>
            ) : (
              <div className="winners-grid">
                {userWinners.map((userWinner, userIndex) => (
                  <div className="user-card" key={userIndex}>
                    <div className="user-card-header">
                      <div className="user-avatar">
                        {userWinner.userInfo?.fullName?.charAt(0) || "U"}
                      </div>
                      <div className="user-info">
                        <h4 className="user-name">
                          {userWinner.userInfo?.fullName || "Unknown User"}
                        </h4>
                        <p className="user-phone">
                          📞 {userWinner.userInfo?.phoneNumber || "N/A"}
                        </p>
                      </div>
                      <div className="user-badge">
                        <span className="badge-count">
                          {userWinner.winners?.length || 0} wins
                        </span>
                      </div>
                    </div>

                    <div className="winners-list">
                      <div className="winners-header">
                        <span className="header-item">Date</span>
                        <span className="header-item">Numbers</span>
                        <span className="header-item">Prize</span>
                        <span className="header-item">Status</span>
                        <span className="header-item">Action</span>
                      </div>

                      {userWinner.winners?.map((winner, winnerIndex) => {
                        const paymentKey = String(winner._id || `${userIndex}-${winnerIndex}`);
                        const isProcessing = processingPayments.has(paymentKey);
                        const isPaid = winner.paid === "Paid";

                        return (
                          <div className="winner-item" key={winner._id || winnerIndex}>
                            <div className="winner-date" data-label="Date">
                              {new Date(winner.NextGameDate).toLocaleDateString()}
                            </div>
                            <div className="winner-numbers" data-label="Numbers">
                              <div className="number-chips">
                                {winner.commonNumbers?.map((num, idx) => (
                                  <span key={idx} className="number-chip">
                                    {num}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="winner-prize" data-label="Prize">
                              <span className="prize-amount">
                                GHC {Number(winner.winPrice || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="winner-status" data-label="Status">
                              <span className={`status-badge ${isPaid ? 'paid' : 'pending'}`}>
                                {isPaid ? '✅ Paid' : '⏳ Pending'}
                              </span>
                              {isSuperAdmin && winner.isHidden === true && (
                                <span
                                  className="status-badge"
                                  style={{ marginLeft: "8px", background: "#f59e0b", color: "#111827" }}
                                >
                                  Hidden
                                </span>
                              )}
                            </div>
                            <div className="winner-action" data-label="Action">
                              <button
                                className={`pay-now-btn ${isPaid ? 'paid' : ''} ${isProcessing ? 'processing' : ''}`}
                                onClick={() => 
                                  handlePayment(
                                    userWinner.userInfo?.phoneNumber,
                                    winner.winPrice,
                                    winner._id
                                  )
                                }
                                disabled={isPaid || isProcessing}
                              >
                                {isProcessing ? (
                                  <>
                                    <span className="spinner"></span>
                                    Processing...
                                  </>
                                ) : isPaid ? (
                                  'Paid'
                                ) : (
                                  'Pay Now'
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-info">
            <p className="footer-text">
              © {new Date().getFullYear()} Lottery Payment System | All rights reserved
            </p>
            <p className="footer-version">Version 2.1.0</p>
          </div>
          <div className="footer-links">
            <a href="#" className="footer-link">Privacy Policy</a>
            <a href="#" className="footer-link">Terms of Service</a>
            <a href="#" className="footer-link">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserPayment;